import { useState, useCallback, useEffect } from 'react';
import { cn } from '@/utils/cn';
import { X, ChevronRight, Pencil, SkipForward } from 'lucide-react';
import type { AskUserQuestion } from '@/tools/ask-user-tool';

interface QuestionSheetProps {
  questions: AskUserQuestion[];
  onComplete: (answers: Record<string, string>) => void;
  onSkip: () => void;
  onDismiss: () => void;
}

export function QuestionSheet({ questions, onComplete, onSkip, onDismiss }: QuestionSheetProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [customInputOpen, setCustomInputOpen] = useState(false);
  const [customText, setCustomText] = useState('');
  const [selectedChoice, setSelectedChoice] = useState<string | null>(null);

  const isInvalid = !Array.isArray(questions) || questions.length === 0;
  const current = isInvalid ? null : questions[currentIndex];
  const currentInvalid = !current || typeof current.question !== 'string';
  const isTextMode = current?.inputType === 'text' || (!current?.choices?.length);

  // Safety: auto-skip if questions data is malformed (e.g. AI sent bad format)
  useEffect(() => {
    if (isInvalid || currentInvalid) {
      onSkip();
    }
  }, [isInvalid, currentInvalid, onSkip]);

  if (isInvalid || currentInvalid) {
    return null;
  }

  const isLast = currentIndex === questions.length - 1;

  // Reset local state when moving between questions
  useEffect(() => {
    setCustomInputOpen(false);
    setCustomText('');
    setSelectedChoice(null);
  }, [currentIndex]);

  const advance = useCallback(
    (answer: string) => {
      const updated = { ...answers, [current.id]: answer };
      setAnswers(updated);

      if (isLast) {
        onComplete(updated);
      } else {
        setCurrentIndex((i) => i + 1);
      }
    },
    [answers, current, isLast, onComplete],
  );

  const handleChoiceClick = useCallback(
    (choice: string) => {
      setSelectedChoice(choice);
      // Brief visual feedback before advancing
      setTimeout(() => advance(choice), 200);
    },
    [advance],
  );

  const handleCustomSubmit = useCallback(() => {
    if (customText.trim()) {
      advance(customText.trim());
    }
  }, [advance, customText]);

  const handleBack = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
    }
  }, [currentIndex]);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40 animate-in fade-in duration-200"
        onClick={onDismiss}
      />

      {/* Sheet */}
      <div className="fixed inset-x-0 bottom-0 z-50 animate-in slide-in-from-bottom duration-300 ease-out">
        <div className="mx-auto max-w-lg bg-background rounded-t-xl shadow-xl border border-border/50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            {/* Progress dots */}
            <div className="flex items-center gap-1.5">
              {questions.map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    'size-1.5 rounded-full transition-colors duration-200',
                    i === currentIndex
                      ? 'bg-primary'
                      : i < currentIndex
                        ? 'bg-primary/40'
                        : 'bg-muted-foreground/20',
                  )}
                />
              ))}
              <span className="ml-2 text-[10px] text-muted-foreground">
                {currentIndex + 1}/{questions.length}
              </span>
            </div>

            <button
              type="button"
              onClick={onDismiss}
              className="size-7 flex items-center justify-center rounded-md hover:bg-muted transition-colors"
              aria-label="Close"
            >
              <X className="size-4 text-muted-foreground" />
            </button>
          </div>

          {/* Question */}
          <div className="px-4 pb-2">
            <h3 className="text-sm font-semibold text-foreground leading-snug">
              {current.question}
            </h3>
          </div>

          {/* Answers */}
          <div className="px-4 pb-2 space-y-1.5 max-h-[45vh] overflow-y-auto">
            {isTextMode ? (
              /* Text input mode */
              <div className="flex gap-2">
                <input
                  type="text"
                  value={customText}
                  onChange={(e) => setCustomText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCustomSubmit();
                  }}
                  placeholder="Type your answer..."
                  autoFocus
                  className="flex-1 text-sm px-3 py-2.5 rounded-lg border border-input bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                <button
                  type="button"
                  onClick={handleCustomSubmit}
                  disabled={!customText.trim()}
                  className="px-3 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-40 transition-colors"
                >
                  OK
                </button>
              </div>
            ) : (
              /* Choices mode */
              <>
                {current.choices!.map((choice, idx) => (
                  <button
                    key={choice}
                    type="button"
                    onClick={() => handleChoiceClick(choice)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-150',
                      'border hover:bg-primary/5 hover:border-primary/30',
                      selectedChoice === choice
                        ? 'bg-primary/10 border-primary/40'
                        : 'border-border/60 bg-muted/30',
                    )}
                  >
                    <span
                      className={cn(
                        'size-6 rounded-md flex items-center justify-center text-xs font-semibold shrink-0',
                        selectedChoice === choice
                          ? 'bg-primary text-white'
                          : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {idx + 1}
                    </span>
                    <span className="text-sm text-foreground flex-1">{choice}</span>
                    {selectedChoice === choice && (
                      <ChevronRight className="size-4 text-primary shrink-0" />
                    )}
                  </button>
                ))}

                {/* Something else option for choice questions */}
                {!customInputOpen ? (
                  <button
                    type="button"
                    onClick={() => setCustomInputOpen(true)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left border border-dashed border-border/60 hover:bg-muted/50 transition-colors"
                  >
                    <span className="size-6 rounded-md flex items-center justify-center bg-muted shrink-0">
                      <Pencil className="size-3 text-muted-foreground" />
                    </span>
                    <span className="text-sm text-muted-foreground">Something else...</span>
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={customText}
                      onChange={(e) => setCustomText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCustomSubmit();
                        if (e.key === 'Escape') setCustomInputOpen(false);
                      }}
                      placeholder="Type your answer..."
                      autoFocus
                      className="flex-1 text-sm px-3 py-2 rounded-lg border border-input bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                    <button
                      type="button"
                      onClick={handleCustomSubmit}
                      disabled={!customText.trim()}
                      className="px-3 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-40 transition-colors"
                    >
                      OK
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-border/40">
            <button
              type="button"
              onClick={handleBack}
              disabled={currentIndex === 0}
              className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
            >
              Back
            </button>

            <button
              type="button"
              onClick={onSkip}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Skip
              <SkipForward className="size-3" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
